import * as ReactDOM from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ErrorBoundary } from 'react-error-boundary';
import { ConfigProvider } from 'antd';
import { PostHogProvider } from 'posthog-js/react';
import posthog, { CapturedNetworkRequest } from 'posthog-js';
import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import dayjs from 'dayjs';
import dayjsLocale from 'dayjs/locale/zh-cn';
import zhCN from 'antd/locale/zh_CN';
// import * as Sentry from '@sentry/react';
// import * as SentryCapacitor from '@sentry/capacitor';
// import { BrowserTracing } from '@sentry/tracing';
import { ConfigIconProvider } from '@fuxi/eevee-icon';
import { LoginProvider } from '@/hooks/useLogin';
import store from '@/store';
import RouterConfig from '@/router';
import { ICON_CDN_LINK } from './constant';
import { sentryTrackEnv } from './utils';
import 'nprogress/nprogress.css';

import '@/styles/index.css';

const POSTHOG_KEYS = {
  qa: 'phc_vzxTgp9WNeHfUly1Fqd9q4yI3PH8kLcmLnlH4ndRK6k',
  pre: 'phc_VitXcQIqFTIlU8y9ceGFdoZbW5FR3GHUANT5SZ9hEEY',
  prod: 'phc_2W3jWHNtLLSFsJzAQMXWBLrVN23XGql3abneAgqoE8V', // 灰度 生产环境共用一个
};

dayjs.locale(dayjsLocale);
const root = ReactDOM.createRoot(document.getElementById('root'));
if (sentryTrackEnv.includes(__BUILD_ENV__)) {
  posthog.init(POSTHOG_KEYS[__BUILD_ENV__], {
    api_host: 'https://us.i.posthog.com',
    disable_session_recording: !__BUILD_ENV__,
    feature_flag_request_timeout_ms: 3000,
    autocapture: true, // 是否捕获用户操作
    capture_pageview: true, // 是否捕获页面加载事件
    capture_pageleave: true, // 是否捕获页面离开事件
    capture_dead_clicks: true, // 是否捕获死点击
    defaults: '2025-11-30',
    enable_recording_console_log: false,
    session_idle_timeout_seconds: 3600,
    disable_web_experiments: false, // 是否禁用 Web 实验功能
    capture_performance: {
      web_vitals: false, // 表示不捕获任何 Web Vitals 指标
    },
    loaded: posthog => {
      App.getInfo().then(async appInfo => {
        await Device.getId().then(info => {
          if (posthog.get_distinct_id() !== info.uuid) {
            posthog.identify(info.uuid, {
              $set_once: { android_id: info.uuid },
              $set: { version: appInfo.version },
            });
          }
        });
      });
      // 页面切换时手动采集
      window.addEventListener('popstate', () => {
        posthog.capture('pageview', { path: window.location.pathname });
      });
    },
    session_recording: {
      recordBody: true, // 表示记录请求体
      recordHeaders: true, // 表示记录请求头
      captureCanvas: {
        canvasFps: 2, // 0-12之间的数字
        canvasQuality: '0.2', // 0-1之间的小数
      }, // 采集画布
      maskCapturedNetworkRequestFn: (request: CapturedNetworkRequest) => {
        // 过滤掉静态资源,只保留 API 请求
        if (
          request.name.includes('.js') ||
          request.name.includes('.css') ||
          request.name.includes('.png') ||
          request.name.includes('.jpg') ||
          request.name.includes('.svg') ||
          request.name.includes('.woff')
        ) {
          return null; // 返回 null 表示忽略这个请求
        }

        // 或者只保留包含 'api' 的请求
        if (!request.name.includes('/api/')) {
          return null;
        }

        return request; // 保留 API 请求
      },
    },
  });
}

root.render(
  <ErrorBoundary
    onError={(error, info) => {
      posthog.captureException(error, info);
    }}
    fallback={
      <h2 style={{ position: 'absolute', inset: 0, paddingTop: '25%', textAlign: 'center' }}>
        Oops, Something went wrong...
      </h2>
    }
  >
    <ConfigProvider locale={zhCN}>
      <ConfigIconProvider cdnLink={ICON_CDN_LINK}>
        <Provider store={store}>
          <Router>
            <PostHogProvider client={posthog}>
              <LoginProvider>
                <RouterConfig />
              </LoginProvider>
            </PostHogProvider>
          </Router>
        </Provider>
      </ConfigIconProvider>
    </ConfigProvider>
  </ErrorBoundary>
);
